import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as ActivityIcon } from "../assets/icons/activity.svg";
import { ReactComponent as ErodeIcon } from "../assets/icons/erode_icon.svg";
import { AssetBreadCrumb } from "../components/AssetBreadCrumb";
import { useDispatch, useSelector } from "react-redux";
import ProfilePictureRounded from "../components/ProfilePictureRounded";
import { MCPService, ChatModelId, FALLBACK_CLIENT_MODEL, codenameForModel } from "../services/mcpService";
import { ConversationService, Conversation } from "../services/conversationService";
import ConversationHistory from "../components/ConversationHistory";
import MessageWithChart from "../components/DataVisualization/MessageWithChart";
import { fetchModelCatalog } from "../store/aiChatSlice";
import { AppDispatch, RootState } from "../store";

import Select from "../components/ReusableComponents/Select";
interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  model?: ChatModelId;
  sessionId?: string; // session ID for conversation tracking
}

const InsightsPage: React.FC = () => {
  const user = useSelector((state: any) => state.user);
  const dispatch = useDispatch<AppDispatch>();
  // Model options are server-owned (GET /mcp/models) — the bundle never names a vendor.
  const modelCatalog = useSelector((state: RootState) => state.aiChat.modelCatalog);
  const modelOptions = modelCatalog?.models ?? [];
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState("");
  const [selectedModel, setSelectedModel] = useState<ChatModelId>(FALLBACK_CLIENT_MODEL);
  const [isLoading, setIsLoading] = useState(false);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [lastEnhancedPrompt, setLastEnhancedPrompt] = useState<string>("");
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [isSavingConversation, setIsSavingConversation] = useState(false);
  const [showMessageLimit, setShowMessageLimit] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [conversationListKey, setConversationListKey] = useState(0);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // This page has its own picker, so it can't rely on the chat panel having loaded the catalog
  useEffect(() => {
    if (!modelCatalog) dispatch(fetchModelCatalog());
  }, [dispatch, modelCatalog]);

  // Follow the server's default once it lands, unless the user already picked something else
  useEffect(() => {
    if (modelCatalog && selectedModel === FALLBACK_CLIENT_MODEL) {
      setSelectedModel(modelCatalog.default);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelCatalog]);

  const handleNewChat = () => {
    setMessages([]);
    setInputText("");
    setLastEnhancedPrompt("");
    setCurrentConversation(null);
    setShowMessageLimit(false);
  };

  const handleConversationSelect = async (conversation: Conversation) => {
    try {
      setIsLoading(true);
      console.log('🔍 Loading conversation:', conversation.id);
      
      const fullConversation = await ConversationService.getConversationWithMessages(conversation.id);
      console.log('🔍 Full conversation result:', fullConversation);
      
      if (fullConversation && fullConversation.messages?.items) {
        console.log('🔍 Messages found:', fullConversation.messages.items.length);
        console.log('🔍 Messages data:', fullConversation.messages.items);
        
        // Convert database messages to UI messages
        const uiMessages: Message[] = fullConversation.messages.items.map(msg => ({
          id: msg.id,
          text: msg.content,
          isUser: msg.isUser,
          timestamp: new Date(msg.createdAt),
          model: msg.model,
          sessionId: msg.sessionId,
        }));
        
        // Sort messages by creation time
        uiMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        
        console.log('🔍 UI messages after conversion:', uiMessages);
        
        setMessages(uiMessages);
        setCurrentConversation(fullConversation);
        setSelectedModel(fullConversation.model);
        setShowMessageLimit(uiMessages.length >= 20);
      } else {
        console.log('🔍 No messages found in conversation');
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    // Check if we've reached the 20-message limit (10 user + 10 AI pairs)
    if (messages.length >= 20) {
      setShowMessageLimit(true);
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText.trim(),
      isUser: true,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputText.trim();
    setInputText("");
    setIsLoading(true);

    try {
      // FIXED: messages.length is stale here - we need to check the previous length before adding the user message
      const isFirstMessage = messages.length === 0; // This was the first message (before we added it)
      console.log('Message handling:', { 
        messagesLengthBefore: messages.length, 
        messagesLengthAfterAdding: messages.length + 1,
        isFirstMessage, 
        currentConversation: !!currentConversation,
        willCreateConversation: isFirstMessage && !currentConversation
      });
      
      // Keep track of which conversation to use for saving messages
      let conversationForSaving = currentConversation;
      
      // Create conversation on first message
      if (isFirstMessage && !currentConversation) {
        console.log('🟢 Creating new conversation!');
        setIsSavingConversation(true);
        try {
          const conversation = await ConversationService.createConversation({
            title: ConversationService.generateTitle(currentInput),
            model: selectedModel,
            modelCodename: codenameForModel(selectedModel, modelCatalog),
          });
          setCurrentConversation(conversation);
          conversationForSaving = conversation; // Use this for saving messages
          
          // Refresh conversation list to show new conversation
          setConversationListKey(prev => prev + 1);
          
          // Save the user message
          await ConversationService.addMessage({
            conversationId: conversation.id,
            content: currentInput,
            isUser: true,
          });
          
          // Update local conversation message count
          conversation.messageCount = 1;
        } catch (error) {
          console.error('Error creating conversation:', error);
        } finally {
          setIsSavingConversation(false);
        }
      } else if (currentConversation) {
        // Save user message to existing conversation
        console.log('🟡 Adding message to existing conversation:', currentConversation.id);
        try {
          await ConversationService.addMessage({
            conversationId: currentConversation.id,
            content: currentInput,
            isUser: true,
          });
          
          // Update local conversation message count
          currentConversation.messageCount += 1;
        } catch (error) {
          console.error('Error saving user message:', error);
        }
      } else {
        console.log('🔴 No conversation created - not first message or conversation exists');
      }
      
      // Prepare chat history (use the messages state before adding current user message)
      const chatHistory = messages.map(msg => ({
        text: msg.text,
        isUser: msg.isUser
      }));
      const lastSessionId =
        [...messages].reverse().find((message) => message.sessionId)?.sessionId ??
        null;
      
      const aiResponse = await MCPService.sendMessage(
        selectedModel, 
        currentInput, 
        null, 
        isFirstMessage, 
        chatHistory,
        lastSessionId
      );
      
      // Capture the enhanced prompt for debugging
      setLastEnhancedPrompt(MCPService.lastEnhancedPrompt);
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: aiResponse.text,
        isUser: false,
        timestamp: new Date(),
        model: selectedModel,
        sessionId: aiResponse.sessionId,
      };
      
      setMessages(prev => [...prev, aiMessage]);
      
      // Save AI response to conversation (use conversationForSaving which will be set if we just created it)
      if (conversationForSaving) {
        console.log('💾 Saving AI response to conversation:', conversationForSaving.id);
        try {
          await ConversationService.addMessage({
            conversationId: conversationForSaving.id,
            content: aiResponse.text,
            isUser: false,
            model: selectedModel,
            modelCodename: codenameForModel(selectedModel, modelCatalog),
            enhancedPrompt: MCPService.lastEnhancedPrompt,
            sessionId: aiResponse.sessionId,
          });
          
          // Update local conversation message count
          conversationForSaving.messageCount = (conversationForSaving.messageCount || 0) + 1;
          
          // Refresh conversation list to update the count display
          setConversationListKey(prev => prev + 1);
        } catch (error) {
          console.error('Error saving AI message:', error);
        }
      } else {
        console.error('❌ No conversation available to save AI message');
      }
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: `Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        isUser: false,
        timestamp: new Date(),
        model: selectedModel,
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex-1 flex bg-white h-screen overflow-hidden">
      {/* Conversation History Sidebar */}
      <ConversationHistory
        key={conversationListKey}
        currentConversationId={currentConversation?.id}
        onConversationSelect={handleConversationSelect}
        onNewConversation={handleNewChat}
      />

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Fixed header section */}
        <div className="flex-shrink-0">
          <AssetBreadCrumb text="Insights">
            <ActivityIcon stroke="#6B7280" className="w-5 h-5 ml-2 mr-1" />
          </AssetBreadCrumb>

        {/* Header section matching other pages */}
        <div className="flex flex-row h-24 border-t border-gray-200 px-10 pt-5 justify-between">
          <div className="flex grow items-center">
            <div className="flex-col items-center justify-center">
              <div className="text-gray-900 text-xl font-semibold font-Inter mb-1">
                Insights
              </div>
              <div className="text-gray-500 text-sm font-normal">
                Chat with AI to get insights about your data and models.
              </div>
            </div>
          </div>
          
          {/* Model Selector, New Chat, and Debug Toggle */}
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-3">
              <span className="text-gray-700 text-sm font-medium">Chat with:</span>
              <Select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="font-medium"
              >
                {modelOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Select>
            </div>
            
            {/* New Chat Button - only show if there are messages */}
            {messages.length > 0 && (
              <button
                onClick={handleNewChat}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                New Chat
              </button>
            )}
            
            {/* Debug Toggle */}
            <button
              onClick={() => setShowDebugInfo(!showDebugInfo)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                showDebugInfo
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              🔍 Debug
            </button>
          </div>
        </div>
      </div>

      {/* Debug Info Panel */}
      {showDebugInfo && (
        <div className="flex-shrink-0 bg-gray-800 text-green-400 p-4 max-h-96 overflow-y-auto">
          {/* System-prompt editing lives in the admin-gated PromptDebugPanel on the AI Analyst page — this route is not admin-gated, so no prompt editor here (issue #694). */}
          {/* Enhanced Prompt Display */}
          {lastEnhancedPrompt && (
            <>
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-bold text-white">🔍 Enhanced Prompt Sent to LLM:</h3>
                <button
                  onClick={() => navigator.clipboard.writeText(lastEnhancedPrompt)}
                  className="text-xs bg-gray-600 hover:bg-gray-500 text-white px-2 py-1 rounded"
                >
                  Copy
                </button>
              </div>
              <pre className="text-xs whitespace-pre-wrap font-mono break-words">
                {lastEnhancedPrompt}
              </pre>
            </>
          )}
        </div>
      )}

      {/* Chat container */}
      <div className="flex-1 flex flex-col bg-gray-50 min-h-0">
        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
          {messages.length === 0 && (
            <div className="flex items-center justify-center h-full px-4">
              <div className="text-center max-w-2xl">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">
                  Actionable insights in an instant.
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Meet your team's newest data expert.
                </p>
                
                {/* Chat starter box */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 max-w-lg mx-auto">
                  <textarea
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="What insights do you need?"
                    className="w-full px-0 py-2 border-none resize-none focus:outline-none text-gray-600 placeholder-gray-400 text-base"
                    rows={3}
                    style={{ minHeight: '80px' }}
                  />
                  <div className="flex justify-between items-center mt-4">
                    <div className="text-xs text-gray-500">
                      Chatting with: <span className="font-medium text-eai-primary-accent">
                        {codenameForModel(selectedModel, modelCatalog)}
                      </span>
                    </div>
                    <button
                      onClick={handleSendMessage}
                      disabled={!inputText.trim() || isLoading}
                      className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                        inputText.trim() && !isLoading
                          ? 'bg-eai-primary-accent text-white hover:bg-opacity-90'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          <span>Thinking...</span>
                        </>
                      ) : (
                        <>
                          <span>✦</span>
                          <span>Generate Insights</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex items-start space-x-2 ${
                message.isUser ? 'justify-end' : 'justify-start'
              }`}
            >
              {!message.isUser && (
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-zinc-900 rounded-full flex items-center justify-center">
                    <ErodeIcon className="w-5 h-5" fill="white" />
                  </div>
                </div>
              )}
              
              <div
                className={`max-w-xs lg:max-w-lg px-4 py-2 rounded-2xl ${
                  message.isUser
                    ? 'bg-eai-primary-accent text-white rounded-br-sm'
                    : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-200'
                }`}
              >
                {message.isUser ? (
                  <div className="text-sm whitespace-pre-wrap">{message.text}</div>
                ) : (
                  <MessageWithChart message={message.text} />
                )}
              </div>

              {message.isUser && (
                <div className="flex-shrink-0">
                  <ProfilePictureRounded
                    pictureURL={user?.permissionDetails?.profilePictureUrl}
                    diameter={32}
                    className="cursor-pointer"
                    onClick={() => {}}
                  />
                </div>
              )}
            </div>
          ))}
          
          {/* "Thinking" indicator while loading */}
          {isLoading && (
            <div className="flex items-start space-x-2 justify-start">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-zinc-900 rounded-full flex items-center justify-center">
                  <ErodeIcon className="w-5 h-5" fill="white" />
                </div>
              </div>
              
              <div className="max-w-xs lg:max-w-md px-4 py-2 rounded-2xl bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-200">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                  <span className="text-sm text-gray-600">Thinking</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Message limit warning */}
          {showMessageLimit && (
            <div className="flex justify-center mt-4">
              <p className="text-gray-400 text-sm">
                You've reached the 20-message limit for this conversation. Please start a new chat to continue.
              </p>
            </div>
          )}
          
          {/* Invisible element to scroll to */}
          <div ref={messagesEndRef} />
        </div>

          {/* Fixed input area - only show after conversation starts */}
          {messages.length > 0 && (
            <div className="flex-shrink-0 border-t border-gray-200 bg-white px-3 pb-3">
              <div className="flex items-end space-x-2">
                <div className="flex-1">
                  <textarea
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about your data, models, or get insights..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-eai-primary-accent focus:border-transparent"
                    rows={1}
                    style={{ 
                      minHeight: '36px',
                      maxHeight: '72px',  // Approximately 2 lines
                      lineHeight: '1.5'
                    }}
                  />
                </div>
                <button
                  onClick={handleSendMessage}
                  disabled={!inputText.trim() || isLoading}
                  className={`px-4 py-2 rounded-xl font-medium transition-colors flex items-center space-x-2 ${
                    inputText.trim() && !isLoading
                      ? 'bg-eai-primary-accent text-white hover:bg-opacity-90'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  style={{ height: '36px' }}
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                      <span className="text-sm">Thinking...</span>
                    </>
                  ) : (
                    <span className="text-sm">Send</span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InsightsPage;
